/**
 * JWT token utilities extracted from portalAuthLoader.ts
 *
 * Provides consistent token validation across the application.
 */

import { jwtDecode } from 'jwt-decode';
import type { JWTPayload, TokenType, TokenValidationResult } from './types';

/**
 * Valid portal token types.
 * - "access": Password-based authentication (90-day expiry)
 * - "session": Magic link authentication (30-day expiry)
 */
const VALID_PORTAL_TOKEN_TYPES: TokenType[] = ['access', 'session', 'portal_access'];

/**
 * Decode a JWT token safely.
 * Returns null if token is malformed or cannot be decoded.
 */
export function decodeToken(token: string): JWTPayload | null {
  try {
    return jwtDecode<JWTPayload>(token);
  } catch (error) {
    console.error('[tokenUtils] Failed to decode token:', error);
    return null;
  }
}

/**
 * Check if a token is expired based on its exp claim.
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded?.exp) {
    return true;
  }
  const expirationMs = decoded.exp * 1000;
  return Date.now() >= expirationMs;
}

/**
 * Get the expiration timestamp from a token in milliseconds.
 * Returns null if token cannot be decoded or has no exp claim.
 */
export function getTokenExpiresAt(token: string): number | null {
  const decoded = decodeToken(token);
  if (!decoded?.exp) {
    return null;
  }
  return decoded.exp * 1000;
}

/**
 * Get time until token expiry in milliseconds.
 * Returns negative value if token is expired.
 */
export function getTimeUntilExpiry(token: string): number | null {
  const expiresAt = getTokenExpiresAt(token);
  if (expiresAt === null) {
    return null;
  }
  return expiresAt - Date.now();
}

/**
 * Validate a portal token for a specific client.
 *
 * Checks:
 * - Token type is valid (access or session)
 * - Token belongs to the expected client
 * - Token has not expired
 */
export function validatePortalToken(
  token: string,
  expectedClientId: string
): TokenValidationResult {
  const decoded = decodeToken(token);

  if (!decoded) {
    return { valid: false, reason: 'malformed' };
  }

  // Accept both "access" (legacy) and "session" (magic link) token types
  if (!decoded.type || !VALID_PORTAL_TOKEN_TYPES.includes(decoded.type)) {
    console.warn('[tokenUtils] Invalid token type:', decoded.type);
    return { valid: false, reason: 'invalid_type' };
  }

  if (decoded.client_id !== expectedClientId) {
    console.warn('[tokenUtils] Token client_id does not match expected clientId');
    return { valid: false, reason: 'wrong_client' };
  }

  if (isTokenExpired(token)) {
    console.log('[tokenUtils] Token has expired');
    return { valid: false, reason: 'expired' };
  }

  return { valid: true };
}

/**
 * Check if a token needs refresh.
 * Returns true if token expires within the given threshold (in minutes).
 */
export function shouldRefreshToken(token: string, thresholdMinutes: number = 10): boolean {
  const timeUntilExpiry = getTimeUntilExpiry(token);
  if (timeUntilExpiry === null) {
    return false;
  }
  return timeUntilExpiry < thresholdMinutes * 60 * 1000;
}
